<template>
  <main class="rh-diagnostic">
    <rh-cabecalho
      pagina="diagnostico"
      rotulo-diagnostico=""
      subtitulo="DIAGNÓSTICO DA EQUIPE"
      titulo=""
      descricao=""
      :unidade-id="unidadeId"
      :empresas="empresas"
      :mostrar-pessoas="podeAcompanhar"
      :mostrar-configuracao="isInRole('rh-diretoria')"
      @empresa-selecionada="selecionarEmpresa"
    />

    <b-message v-if="erro" type="is-danger" aria-close-label="Fechar" @close="erro = ''">{{ erro }}</b-message>
    <b-loading :active="loading" :is-full-page="false"></b-loading>
    <div v-if="!instrumentoPronto" class="rh-notice">
      O instrumento ainda não foi configurado com 9 dimensões e 36 afirmações. Peça à diretoria para cadastrar o método
      antes de abrir ciclos.
    </div>
    <div v-if="podeAcompanhar && !instrumento7DPronto" class="rh-notice">Configure o instrumento 7D com 7 dimensões e 28 afirmações antes de abrir novos ciclos.</div>
    <div v-if="politicaPendente" class="rh-notice">
      Configure o mínimo de respostas e as faixas do farol em Configuração antes de abrir ciclos ou consultar o mapa de
      calor.
    </div>

    <template v-if="podeAcompanhar && unidadeId">
      <section class="rh-card rh-toolbar">
        <b-field label="Equipe"
          ><b-select v-model="equipeId" expanded placeholder="Selecione uma equipe" @input="carregarCiclos"
            ><option v-for="x in equipes" :key="x.id" :value="x.id">{{ x.nome }} · {{ x.area }}</option></b-select
          ></b-field
        >
        <b-button
          type="is-primary"
          icon-left="plus"
          :disabled="!equipeId || !instrumentoPronto || !instrumento7DPronto || !equipeComLider || politicaPendente"
          @click="abrirCriacao"
          >Abrir ciclo</b-button
        >
      </section>
      <div v-if="equipeId && !equipeComLider" class="rh-notice">
        Atribua um líder à equipe na tela Estrutura antes de abrir o diagnóstico.
      </div>
      <div v-if="!equipes.length && !loading" class="rh-card rh-empty">
        Nenhuma equipe disponível para o seu perfil. Configure a estrutura antes de abrir um ciclo.
      </div>
      <b-tabs v-model="abaAtiva" type="is-boxed" multiline class="rh-diagnostic-tabs">
        <b-tab-item value="ciclos" label="Ciclo">
          <section v-if="equipeId" class="rh-card">
            <div class="rh-card-head">
              <div>
                <p class="rh-kicker">COLETA</p>
                <h2>Ciclos da equipe</h2>
              </div>
              <span class="rh-count">{{ ciclos.length }} ciclos</span>
            </div>
            <b-table :data="ciclos" :loading="loading" paginated :per-page="10" hoverable mobile-cards>
              <b-table-column v-slot="p" field="nome" label="Ciclo" sortable
                ><strong>{{ p.row.nome }}</strong></b-table-column
              >
              <b-table-column v-slot="p" field="inicioEm" label="Início">{{ dataCurta(p.row.inicioEm) }}</b-table-column>
              <b-table-column v-slot="p" field="fimEm" label="Término">{{ dataCurta(p.row.fimEm) }}</b-table-column>
              <b-table-column v-slot="p" field="aberto" label="Status"
                ><b-tag :type="p.row.aberto ? 'is-info' : 'is-light'">{{
                  p.row.aberto ? 'Aberto' : 'Encerrado'
                }}</b-tag></b-table-column
              >
              <b-table-column v-slot="p" field="respostas" label="Adesão">9D: {{ p.row.respostas }} · 7D: {{ p.row.tem7D ? p.row.respostas7D : '—' }}</b-table-column>
              <b-table-column v-slot="p" label="Ações"
                ><b-button size="is-small" type="is-light" icon-left="chart-bar" @click="verResultado(p.row)"
                  >Resultado 9D</b-button
                ><b-button v-if="p.row.tem7D" size="is-small" type="is-light" icon-left="chart-bar" @click="verResultado7D(p.row)">Resultado 7D</b-button
                ><b-button
                  v-if="p.row.ativo"
                  size="is-small"
                  type="is-light"
                  icon-left="link-variant"
                  @click="copiarLink(p.row)"
                  >Link</b-button
                ><b-button
                  v-if="p.row.aberto"
                  size="is-small"
                  type="is-light"
                  icon-left="stop-circle-outline"
                  @click="confirmarEncerramento(p.row)"
                  >Encerrar</b-button
                ></b-table-column
              >
              <template #empty><div class="rh-empty">Nenhum ciclo criado para esta equipe.</div></template>
            </b-table>
          </section>
        </b-tab-item>
        <b-tab-item value="resultados" label="Resultados">
          <div v-if="!resultado && !resultado7D" class="rh-card rh-empty">Selecione um ciclo na aba Ciclos para consultar o resultado.</div>
          <RhResultado7D v-if="resultado7D" :resultado="resultado7D" />
          <section v-if="resultado" class="rh-card rh-results">
            <div class="rh-card-head">
              <div>
                <p class="rh-kicker">SAÚDE DA EQUIPE</p>
                <h2>{{ resultado.equipe }}</h2>
              </div>
              <div class="rh-export-actions">
                <b-button
                  v-if="resultado.media !== null"
                  size="is-small"
                  icon-left="file-excel"
                  @click="
                    baixarExcel(
                      `/api/rh/diagnostico/ciclos/${resultado.cicloId}/resultado-excel`,
                      `rh_resultado_${resultado.cicloId}.xlsx`
                    )
                  "
                  >Excel</b-button
                ><b-button size="is-small" @click="resultado = null">Fechar</b-button>
              </div>
            </div>
            <div class="rh-result-top">
              <div>
                <span>Participações</span><strong>{{ resultado.respostas }}</strong
                ><small>Mínimo para leitura: {{ resultado.minimoRespostas }}</small>
              </div>
              <div v-if="resultado.media !== null">
                <span>Média geral</span><strong>{{ formatarNota(resultado.media) }}</strong
                ><b-tag :type="tipoFarol(resultado.farol)">{{ textoFarol(resultado.farol) }}</b-tag>
              </div>
            </div>
            <div v-if="resultado.media === null" class="rh-notice">
              Resultado reservado até o encerramento do ciclo e o mínimo de respostas. Nenhuma nota individual é exibida.
            </div>
            <div v-else class="rh-dimensions">
              <div v-for="d in resultado.dimensoes" :key="d.id" class="rh-dimension">
                <span>{{ d.nome }}</span>
                <div class="rh-track"><i :style="{ width: (Number(d.media) / 5) * 100 + '%' }"></i></div>
                <strong>{{ formatarNota(d.media) }}</strong>
              </div>
            </div>
          </section>
        </b-tab-item>
        <b-tab-item value="evolucao" label="Evolução">
          <div v-if="!evolucao.length && ciclos.length < 2" class="rh-card rh-empty">
            O histórico e a comparação estarão disponíveis após a criação de mais ciclos.
          </div>
          <section v-if="evolucao.length" class="rh-card">
            <div class="rh-card-head">
              <div>
                <p class="rh-kicker">EVOLUÇÃO NO TEMPO</p>
                <h2>Histórico da equipe</h2>
              </div>
            </div>
            <p class="rh-muted">Somente ciclos encerrados com o mínimo de respostas aparecem aqui.</p>
            <div v-for="ciclo in evolucao" :key="ciclo.id" class="rh-trend-row">
              <span>{{ ciclo.nome }} · {{ dataCurta(ciclo.inicioEm) }}</span>
              <div class="rh-track"><i :style="{ width: (Number(ciclo.media) / 5) * 100 + '%' }"></i></div>
              <strong>{{ formatarNota(ciclo.media) }}</strong
              ><b-tag :type="tipoFarol(ciclo.farol)">{{ textoFarol(ciclo.farol) }}</b-tag>
            </div>
          </section>
          <section v-if="ciclos.length >= 2" class="rh-card">
            <div class="rh-card-head">
              <div>
                <p class="rh-kicker">PROVA POR DADOS</p>
                <h2>Antes × depois</h2>
              </div>
            </div>
            <div class="rh-compare-picker">
              <b-select v-model="antesId" placeholder="Ciclo anterior" expanded
                ><option v-for="x in ciclos" :key="x.id" :value="x.id">{{ x.nome }}</option></b-select
              ><b-select v-model="depoisId" placeholder="Ciclo posterior" expanded
                ><option v-for="x in ciclos" :key="x.id" :value="x.id">{{ x.nome }}</option></b-select
              ><b-button type="is-primary" :disabled="!antesId || !depoisId" @click="comparar">Comparar</b-button>
            </div>
            <b-tabs v-model="abaComparacao" type="is-boxed" class="rh-compare-tabs">
              <b-tab-item value="tabela" label="Tabela">
                <div v-if="comparacao && !comparacao.disponivel" class="rh-notice">
                  A comparação será exibida quando ambos os ciclos atingirem o mínimo de respostas.
                </div>
                <div v-if="comparacao && comparacao.disponivel">
                  <p class="rh-variation">
                    Variação geral:
                    <strong :class="comparacao.variacao >= 0 ? 'positive' : 'negative'"
                      >{{ sinal(comparacao.variacao) }}{{ formatarNota(comparacao.variacao) }}</strong
                    >
                  </p>
                  <b-table :data="comparacao.dimensoes" mobile-cards
                    ><b-table-column v-slot="p" field="nome" label="Dimensão">{{ p.row.nome }}</b-table-column
                    ><b-table-column v-slot="p" field="antes" label="Antes">{{ formatarNota(p.row.antes) }}</b-table-column
                    ><b-table-column v-slot="p" field="depois" label="Depois">{{ formatarNota(p.row.depois) }}</b-table-column
                    ><b-table-column v-slot="p" field="variacao" label="Variação"
                      ><strong :class="p.row.variacao >= 0 ? 'positive' : 'negative'"
                        >{{ sinal(p.row.variacao) }}{{ formatarNota(p.row.variacao) }}</strong
                      ></b-table-column
                    ></b-table
                  >
                  <div v-if="isInRole('rh-diretoria')" class="rh-export-actions">
                    <b-button type="is-primary" icon-left="file-pdf-box" @click="baixarRelatorio('pdf')">Exportar PDF</b-button
                    ><b-button type="is-light" icon-left="file-excel" @click="baixarRelatorio('xlsx')">Exportar Excel</b-button>
                  </div>
                </div>
              </b-tab-item>
              <b-tab-item value="barras" label="Barras">
                <div v-if="comparacao && !comparacao.disponivel" class="rh-notice">
                  A comparação será exibida quando ambos os ciclos atingirem o mínimo de respostas.
                </div>
                <p v-if="comparacao && comparacao.disponivel" class="rh-variation">
                  Variação geral:
                  <strong :class="comparacao.variacao >= 0 ? 'positive' : 'negative'"
                    >{{ sinal(comparacao.variacao) }}{{ formatarNota(comparacao.variacao) }}</strong
                  >
                </p>
                <div v-if="comparacao && comparacao.disponivel" class="rh-compare-bars">
                  <p class="rh-bars-legend">O traço indica a nota do ciclo anterior.</p>
                  <div v-for="dimensao in comparacao.dimensoes" :key="dimensao.id" class="rh-bar-row">
                    <div class="rh-bar-heading">
                      <span class="rh-bar-name">{{ dimensao.nome }}</span>
                      <span class="rh-bar-values">
                        <span :class="dimensao.variacao >= 0 ? 'positive' : 'negative'"
                          >{{ sinal(dimensao.variacao) }}{{ formatarNotaBarra(dimensao.variacao) }}</span
                        >
                        <strong>{{ formatarNotaBarra(dimensao.depois) }}</strong>
                      </span>
                    </div>
                    <div
                      class="rh-bar-track"
                      role="progressbar"
                      :aria-label="`${dimensao.nome}: nota atual`"
                      :aria-valuenow="Number(dimensao.depois)"
                      aria-valuemin="0"
                      aria-valuemax="5"
                    >
                      <span
                        class="rh-bar-fill"
                        :class="classeBarra(dimensao.depois)"
                        :style="{ width: percentualNota(dimensao.depois) + '%' }"
                      ></span>
                      <span class="rh-bar-previous" :style="{ left: percentualNota(dimensao.antes) + '%' }"></span>
                    </div>
                  </div>
                </div>
              </b-tab-item>
              <b-tab-item value="radar" label="Radar">
                <div v-if="comparacao && !comparacao.disponivel" class="rh-notice">
                  A comparação será exibida quando ambos os ciclos atingirem o mínimo de respostas.
                </div>
                <div v-else-if="!comparacao" class="rh-empty">Selecione dois ciclos e clique em Comparar.</div>
                <p v-if="comparacao && comparacao.disponivel" class="rh-variation">
                  Variação geral:
                  <strong :class="comparacao.variacao >= 0 ? 'positive' : 'negative'"
                    >{{ sinal(comparacao.variacao) }}{{ formatarNota(comparacao.variacao) }}</strong
                  >
                </p>
                <div v-if="comparacao && comparacao.disponivel" class="rh-radar-panel">
                  <div class="rh-radar-scroll">
                    <svg
                      ref="radarComparacao"
                      class="rh-radar-chart"
                      role="img"
                      :aria-label="`Gráfico radar comparando ${nomeCiclo(comparacao.antes.cicloId)} e ${nomeCiclo(comparacao.depois.cicloId)} nas dimensões do diagnóstico`"
                    ></svg>
                  </div>
                  <div class="rh-radar-legend">
                    <span><i class="rh-radar-key current"></i>{{ nomeCiclo(comparacao.depois.cicloId) }}</span>
                    <span><i class="rh-radar-key previous"></i>{{ nomeCiclo(comparacao.antes.cicloId) }}</span>
                  </div>
                </div>
              </b-tab-item>
            </b-tabs>
          </section>
        </b-tab-item>
        <b-tab-item v-if="podeGerirEmpresa" value="empresa" label="Visão da empresa">
          <div v-if="!mapa && equipes.length <= 1" class="rh-card rh-empty">
            Os dados da empresa aparecerão quando houver resultados divulgados.
          </div>
          <section v-if="mapa && (isInRole('rh-diretoria'))" class="rh-card">
            <div class="rh-card-head">
              <div>
                <p class="rh-kicker">VISÃO DA EMPRESA</p>
                <h2>Mapa de calor por área</h2>
              </div>
              <b-button
                size="is-small"
                icon-left="file-excel"
                @click="baixarExcel('/api/rh/diagnostico/mapa-calor-excel', `rh_mapa_calor_${unidadeId}.xlsx`)"
                >Exportar Excel</b-button
              >
            </div>
            <p class="rh-muted">
              Cada área mostra as médias dos ciclos mais recentes das equipes que atingiram o mínimo de respostas.
            </p>
            <div class="rh-heat-scroll">
              <table class="table is-fullwidth rh-heat">
                <thead>
                  <tr>
                    <th>Área</th>
                    <th v-for="d in mapa.dimensoes" :key="d.id">{{ d.nome }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="area in mapa.areas" :key="area.areaId">
                    <th>
                      {{ area.area }}<small>{{ area.equipes }} equipes · {{ area.respostas }} respostas</small>
                    </th>
                    <td v-for="d in mapa.dimensoes" :key="d.id">
                      <span v-if="area.notas[d.id]" class="rh-heat-cell" :class="classeCalor(area.notas[d.id])">{{
                        formatarNota(area.notas[d.id])
                      }}</span
                      ><span v-else class="rh-heat-empty">—</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
          <section v-if="podeGerirEmpresa && equipes.length > 1" class="rh-card">
            <div class="rh-card-head">
              <div>
                <p class="rh-kicker">VISÃO COMPARATIVA</p>
                <h2>Entre equipes</h2>
              </div>
            </div>
            <p class="rh-muted">
              Selecione um ciclo encerrado de cada equipe. As médias só aparecem quando ambos atingiram o mínimo de
              respostas.
            </p>
            <div class="rh-compare-picker">
              <b-select v-model="cicloAId" placeholder="Equipe e ciclo A" expanded
                ><option v-for="x in ciclosEmpresa" :key="x.id" :value="x.id">
                  {{ x.equipe }} · {{ x.nome }} · {{ dataCurta(x.inicioEm) }}
                </option></b-select
              ><b-select v-model="cicloBId" placeholder="Equipe e ciclo B" expanded
                ><option v-for="x in ciclosEmpresa" :key="x.id" :value="x.id">
                  {{ x.equipe }} · {{ x.nome }} · {{ dataCurta(x.inicioEm) }}
                </option></b-select
              ><b-button type="is-primary" :disabled="!cicloAId || !cicloBId" @click="compararEquipes"
                >Comparar equipes</b-button
              >
            </div>
            <div v-if="comparacaoEquipes && !comparacaoEquipes.disponivel" class="rh-notice">
              Ambos os ciclos precisam estar encerrados e ter o mínimo de respostas.
            </div>
            <div v-if="comparacaoEquipes && comparacaoEquipes.disponivel">
              <p class="rh-variation">
                {{ comparacaoEquipes.equipeA }}: {{ formatarNota(comparacaoEquipes.mediaA) }} ·
                {{ comparacaoEquipes.equipeB }}: {{ formatarNota(comparacaoEquipes.mediaB) }}
              </p>
              <b-table :data="comparacaoEquipes.dimensoes" mobile-cards
                ><b-table-column v-slot="p" field="nome" label="Dimensão">{{ p.row.nome }}</b-table-column
                ><b-table-column v-slot="p" field="mediaA" :label="comparacaoEquipes.equipeA">{{
                  formatarNota(p.row.mediaA)
                }}</b-table-column
                ><b-table-column v-slot="p" field="mediaB" :label="comparacaoEquipes.equipeB">{{
                  formatarNota(p.row.mediaB)
                }}</b-table-column></b-table
              >
            </div>
          </section>
        </b-tab-item>
      </b-tabs>
    </template>

    <template v-if="(isInRole('rh-colaborador') || isInRole('rh-lider')) && unidadeId">
      <section class="rh-card">
        <div class="rh-card-head">
          <div>
            <p class="rh-kicker">SUA PARTICIPAÇÃO</p>
            <h2>Diagnósticos disponíveis</h2>
          </div>
        </div>
        <p class="rh-muted">Responda com sinceridade. Suas notas não serão mostradas individualmente.</p>
        <b-table :data="meusCiclos" mobile-cards
          ><b-table-column v-slot="p" field="nome" label="Diagnóstico">{{ p.row.nome }}</b-table-column
          ><b-table-column v-slot="p" field="tipo" label="Instrumento">{{ p.row.tipo.toUpperCase() }}</b-table-column
          ><b-table-column v-slot="p" field="equipe" label="Equipe">{{ p.row.equipe }}</b-table-column
          ><b-table-column v-slot="p" field="fimEm" label="Até">{{ dataCurta(p.row.fimEm) }}</b-table-column
          ><b-table-column v-slot="p" label="Responder"
            ><b-button type="is-primary" size="is-small" @click="abrirQuestionario(p.row)"
              >Responder</b-button
            ></b-table-column
          ><template #empty
            ><div class="rh-empty">Nenhum diagnóstico pendente. Obrigado por participar.</div></template
          ></b-table
        >
      </section>
      <section v-if="questionario" class="rh-card rh-questionnaire" id="rh-questionario">
        <p class="rh-kicker">{{ questionario.autoavaliacao ? 'AUTOAVALIAÇÃO DO LÍDER' : 'RESPOSTA ANÔNIMA' }}</p>
        <h2>{{ questionario.ciclo }} · {{ tipoResposta.toUpperCase() }}</h2>
        <p class="rh-muted">Para cada afirmação, escolha de 1 (discordo totalmente) a 5 (concordo totalmente).</p>
        <div v-for="(dimensao, di) in questionario.dimensoes" :key="di" class="rh-question-group">
          <h3>{{ dimensao.nome }}</h3>
          <div v-for="(questao, qi) in dimensao.questoes" :key="questao.id" class="rh-question">
            <p>{{ qi + 1 }}. {{ questao.texto }}</p>
            <div class="rh-scale">
              <label v-for="nota in [1, 2, 3, 4, 5]" :key="nota"
                ><input
                  type="radio"
                  :name="'q' + questao.id"
                  :value="nota"
                  v-model.number="notas[questao.id]"
                /><span>{{ nota }}</span></label
              >
            </div>
          </div>
        </div>

        <section class="rh-confirmacao" role="group" aria-labelledby="rh-confirmacao-titulo" aria-describedby="rh-confirmacao-texto">
          <div class="rh-confirmacao-head">
            <b-icon icon="shield-check" size="is-small" aria-hidden="true"></b-icon>
            <div>
              <p class="rh-confirmacao-kicker">ANTES DE ENVIAR</p>
              <h3 id="rh-confirmacao-titulo">Confirmação de participação</h3>
            </div>
          </div>
          <p id="rh-confirmacao-texto" class="rh-confirmacao-texto">
            {{ questionario.autoavaliacao ? 'Entendo que minha autoavaliação será apresentada separadamente da média da equipe.' : 'Entendo que as respostas serão usadas em resultados agregados da equipe, respeitando o mínimo de participantes.' }}
          </p>
          <div class="rh-confirmacao-opcoes">
            <b-radio v-model="consentimento" :native-value="true">Sim, estou ciente</b-radio>
            <b-radio v-model="consentimento" :native-value="false">Não quero enviar agora</b-radio>
          </div>
        </section>
        <div class="rh-submit">
          <span>{{ respostasPreenchidas }} de {{ totalQuestoes }} respondidas</span
          ><b-button
            type="is-primary"
            :disabled="respostasPreenchidas !== totalQuestoes || consentimento !== true"
            :loading="salvando"
            @click="enviarRespostas"
            >Enviar respostas</b-button
          >
        </div>
      </section>
    </template>

    <b-modal :active.sync="modalCriacao" has-modal-card trap-focus aria-role="dialog" aria-modal>
      <ValidationObserver v-slot="{ handleSubmit }" slim
        ><form class="modal-card" @submit.prevent="handleSubmit(criarCiclo)">
          <header class="modal-card-head"><p class="modal-card-title">Abrir ciclo</p></header>
          <section class="modal-card-body">
            <b-message v-if="erroCriacao" type="is-danger" aria-close-label="Fechar" @close="erroCriacao = ''">{{ erroCriacao }}</b-message>
            <ValidationProvider v-slot="{ errors }" name="Nome" rules="required|min:2|max:120"
              ><b-field label="Nome do ciclo" :type="errors.length ? 'is-danger' : ''" :message="errors[0]"
                ><b-input
                  v-model.trim="novoCiclo.nome"
                  maxlength="120"
                  placeholder="Ex.: Diagnóstico inicial"
                ></b-input></b-field></ValidationProvider
            ><b-field label="Início"
              ><input v-model="novoCiclo.inicioEm" type="datetime-local" class="input" required /></b-field
            ><b-field label="Término"
              ><input v-model="novoCiclo.fimEm" type="datetime-local" class="input" required
            /></b-field>
            <p class="rh-muted">
              Após aberto, o ciclo ficará disponível para os membros da equipe no período informado.
            </p>
          </section>
          <footer class="modal-card-foot">
            <b-button @click="modalCriacao = false">Cancelar</b-button
            ><b-button native-type="submit" type="is-primary" :loading="salvando">Abrir ciclo</b-button>
          </footer>
        </form></ValidationObserver
      >
    </b-modal>
  </main>
</template>

<script>
import http from '@/http'
import RhCabecalho from '@/components/rh_cabecalho.vue'
import RhResultado7D from '@/components/rh_resultado_7d.vue'
import { select, lineRadial, curveLinearClosed, scaleLinear } from 'd3'
import { mapGetters } from 'vuex'

export default {
  name: 'RhDiagnostico',
  components: { RhCabecalho, RhResultado7D },
  data() {
    return {
      empresas: [],
      unidadeId: null,
      equipes: [],
      equipeId: null,
      abaAtiva: 'ciclos',
      abaComparacao: 'tabela',
      ciclos: [],
      ciclosEmpresa: [],
      evolucao: [],
      meusCiclos: [],
      mapa: null,
      politicaPendente: false,
      instrumentoPronto: false,
      instrumento7DPronto: false,
      resultado: null,
      resultado7D: null,
      comparacao: null,
      comparacaoEquipes: null,
      antesId: null,
      depoisId: null,
      cicloAId: null,
      cicloBId: null,
      questionario: null,
      cicloRespostaId: null,
      tipoResposta: '9d',
      notas: {},
      consentimento: null,
      novoCiclo: { nome: '', inicioEm: '', fimEm: '' },
      modalCriacao: false,
      loading: false,
      salvando: false,
      erro: '',
      erroCriacao: '',
    }
  },
  computed: {
    ...mapGetters(['isInRole']),
    podeAcompanhar() {
      return this.isInRole('rh-diretoria') || this.isInRole('rh-lider')
    },
    podeGerirEmpresa() {
      return this.isInRole('rh-diretoria')
    },
    equipeComLider() {
      const equipe = this.equipes.find((x) => x.id === this.equipeId)
      return !!(equipe && equipe.liderId)
    },
    respostasPreenchidas() {
      return Object.keys(this.notas).filter((k) => this.notas[k] >= 1 && this.notas[k] <= 5).length
    },
    totalQuestoes() {
      return this.questionario ? this.questionario.dimensoes.reduce((total, d) => total + d.questoes.length, 0) : 0
    },
  },
  watch: {
    abaComparacao(aba) {
      if (aba === 'radar') this.$nextTick(this.renderizarRadar)
    },
    comparacao() {
      if (this.abaComparacao === 'radar') this.$nextTick(this.renderizarRadar)
    },
  },
  created() {
    this.iniciar()
  },
  methods: {
    selecionarEmpresa(id) {
      this.unidadeId = id
      this.carregarEmpresa()
    },
    falha(e) {
      this.erro = e && e.data && e.data.erro ? e.data.erro : 'Não foi possível concluir a operação. Tente novamente.'
    },
    dataCurta(v) {
      return v ? new Date(v).toLocaleDateString('pt-BR') : '—'
    },
    formatarNota(v) {
      return Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    formatarNotaBarra(v) {
      return Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
    },
    percentualNota(v) {
      return Math.max(0, Math.min(100, (Number(v) / 5) * 100))
    },
    classeBarra(v) {
      if (Number(v) < Number(this.comparacao.corteCritico)) return 'critical'
      if (Number(v) < Number(this.comparacao.corteSaudavel)) return 'attention'
      return 'healthy'
    },
    nomeCiclo(id) {
      const ciclo = this.ciclos.find((x) => x.id === id)
      return ciclo ? ciclo.nome : `Ciclo ${id}`
    },
    renderizarRadar() {
      const elemento = this.$refs.radarComparacao
      const comparacao = this.comparacao
      if (!elemento || this.abaComparacao !== 'radar' || !comparacao || !comparacao.disponivel) return

      const dimensoes = comparacao.dimensoes || []
      const svg = select(elemento)
      svg.selectAll('*').remove()
      if (dimensoes.length < 3) return

      const raio = 150
      const angulo = (indice) => (indice * 2 * Math.PI) / dimensoes.length
      const ponto = (indice, distancia) => [Math.sin(angulo(indice)) * distancia, -Math.cos(angulo(indice)) * distancia]
      const escala = scaleLinear().domain([0, 5]).range([0, raio]).clamp(true)
      const linha = lineRadial()
        .angle((_, indice) => angulo(indice))
        .radius((nota) => escala(nota))
        .curve(curveLinearClosed)
      const grafico = svg.attr('viewBox', '0 0 900 480').append('g').attr('transform', 'translate(450,220)')

      for (const nivel of [1, 2, 3, 4, 5]) {
        grafico
          .append('path')
          .attr('d', linha(dimensoes.map(() => nivel)))
          .attr('fill', 'none')
          .attr('stroke', '#3b3e4d')
          .attr('stroke-width', 1)
      }

      grafico
        .selectAll('.rh-radar-axis')
        .data(dimensoes)
        .enter()
        .append('line')
        .attr('x1', 0)
        .attr('y1', 0)
        .attr('x2', (_, indice) => ponto(indice, raio)[0])
        .attr('y2', (_, indice) => ponto(indice, raio)[1])
        .attr('stroke', '#3b3e4d')

      grafico
        .append('path')
        .attr('d', linha(dimensoes.map((d) => Number(d.antes))))
        .attr('fill', 'none')
        .attr('stroke', '#a9aec1')
        .attr('stroke-width', 2)
        .attr('stroke-dasharray', '5 5')

      grafico
        .append('path')
        .attr('d', linha(dimensoes.map((d) => Number(d.depois))))
        .attr('fill', 'rgba(162, 143, 239, 0.22)')
        .attr('stroke', '#a792ed')
        .attr('stroke-width', 2.5)

      const cores = { critical: '#e9787f', attention: '#e7be66', healthy: '#71caa8' }
      grafico
        .selectAll('.rh-radar-point')
        .data(dimensoes)
        .enter()
        .append('circle')
        .attr('cx', (d, indice) => ponto(indice, escala(Number(d.depois)))[0])
        .attr('cy', (d, indice) => ponto(indice, escala(Number(d.depois)))[1])
        .attr('r', 5.5)
        .attr('fill', (d) => cores[this.classeBarra(d.depois)])
        .attr('stroke', '#171924')
        .attr('stroke-width', 2)
        .append('title')
        .text((d) => `${d.nome}: ${this.formatarNotaBarra(d.depois)} (anterior: ${this.formatarNotaBarra(d.antes)})`)

      grafico
        .selectAll('.rh-radar-label')
        .data(dimensoes)
        .enter()
        .append('text')
        .attr('x', (_, indice) => ponto(indice, raio + 34)[0])
        .attr('y', (_, indice) => ponto(indice, raio + 34)[1])
        .attr('text-anchor', (_, indice) => {
          const x = ponto(indice, raio + 34)[0]
          return x > 18 ? 'start' : x < -18 ? 'end' : 'middle'
        })
        .attr('dominant-baseline', 'middle')
        .attr('fill', '#e5e5eb')
        .attr('font-size', 15)
        .text((d) => `${d.nome} ${this.formatarNotaBarra(d.depois)}`)
    },
    sinal(v) {
      return Number(v) > 0 ? '+' : ''
    },
    tipoFarol(v) {
      return v === 'saudavel' ? 'is-success' : v === 'atencao' ? 'is-warning' : 'is-danger'
    },
    textoFarol(v) {
      return v === 'saudavel' ? 'Saudável' : v === 'atencao' ? 'Atenção' : 'Crítico'
    },
    classeCalor(v) {
      return Number(v) < Number(this.mapa.corteCritico)
        ? 'critical'
        : Number(v) < Number(this.mapa.corteSaudavel)
        ? 'attention'
        : 'healthy'
    },
    async iniciar() {
      this.loading = true
      try {
        const [empresas, instrumento] = await Promise.all([
          http.get('/api/rh/estrutura/empresas'),
          http.get('/api/rh/diagnostico/instrumento'),
        ])
        this.empresas = empresas.data || []
        this.instrumentoPronto = !!instrumento.data.pronto
        if (this.podeAcompanhar) {
          const instrumento7D = await http.get('/api/rh/diagnostico/instrumento-7d')
          this.instrumento7DPronto = !!instrumento7D.data.pronto
        }
        const atual = Number(this.$store.state.unidadeId)
        this.unidadeId = this.empresas.some((x) => x.id === atual)
          ? atual
          : (this.empresas[0] && this.empresas[0].id) || null
        const unidadeLink = Number(this.$route.query.unidadeId)
        if (this.empresas.some((x) => x.id === unidadeLink)) this.unidadeId = unidadeLink
        if (this.unidadeId) await this.carregarEmpresa()
      } catch (e) {
        this.falha(e)
      } finally {
        this.loading = false
      }
    },
    async carregarEmpresa() {
      this.abaAtiva = 'ciclos'
      this.abaComparacao = 'tabela'
      this.resultado = null
      this.resultado7D = null
      this.comparacao = null
      this.comparacaoEquipes = null
      this.questionario = null
      this.mapa = null
      this.politicaPendente = false
      this.ciclos = []
      this.ciclosEmpresa = []
      this.evolucao = []
      this.equipes = []
      if (!this.unidadeId) return
      this.loading = true
      try {
        if (this.podeAcompanhar) {
          this.equipes =
            (await http.get('/api/rh/estrutura/equipes', { params: { unidadeId: this.unidadeId } })).data || []
          this.equipeId = (this.equipes[0] && this.equipes[0].id) || null
          if (this.equipeId) await this.carregarCiclos()
        }
        if (this.podeGerirEmpresa) {
          try {
            this.mapa = (
              await http.get('/api/rh/diagnostico/mapa-calor', { params: { unidadeId: this.unidadeId } })
            ).data
          } catch (e) {
            if (e.status === 400 && e.data && e.data.erro && e.data.erro.includes('política de divulgação'))
              this.politicaPendente = true
            else throw e
          }
          const listas = await Promise.all(
            this.equipes.map((equipe) =>
              http.get('/api/rh/diagnostico/ciclos', { params: { unidadeId: this.unidadeId, equipeId: equipe.id } })
            )
          )
          this.ciclosEmpresa = listas.reduce(
            (todos, resposta, indice) =>
              todos.concat(
                (resposta.data || []).filter((c) => !c.aberto).map((c) => ({ ...c, equipe: this.equipes[indice].nome }))
              ),
            []
          )
        }
        if (this.isInRole('rh-colaborador') || this.isInRole('rh-lider')) {
          await this.carregarMeusCiclos()
          const cicloLink = Number(this.$route.query.cicloId)
          const tipoLink = this.$route.query.tipo || '9d'
          const pendente = this.meusCiclos.find((x) => x.id === cicloLink && x.tipo === tipoLink)
          if (pendente) await this.abrirQuestionario(pendente)
        }
      } catch (e) {
        this.falha(e)
      } finally {
        this.loading = false
      }
    },
    async carregarCiclos() {
      this.resultado = null
      this.resultado7D = null
      this.comparacao = null
      this.abaComparacao = 'tabela'
      this.evolucao = []
      try {
        if (!this.equipeId) {
          this.ciclos = []
          return
        }
        const params = { unidadeId: this.unidadeId, equipeId: this.equipeId }
        const [ciclos, evolucao] = await Promise.all([
          http.get('/api/rh/diagnostico/ciclos', { params }),
          http.get('/api/rh/diagnostico/evolucao', { params }),
        ])
        this.ciclos = ciclos.data || []
        this.evolucao = evolucao.data || []
      } catch (e) {
        this.falha(e)
      }
    },
    async carregarMeusCiclos() {
      this.meusCiclos =
        (await http.get('/api/rh/diagnostico/meus-ciclos', { params: { unidadeId: this.unidadeId } })).data || []
    },
    async copiarLink(ciclo) {
      const link = `${window.location.origin}/rh/diagnostico?unidadeId=${this.unidadeId}&cicloId=${ciclo.id}`
      try {
        await navigator.clipboard.writeText(link)
        this.$buefy.toast.open({
          message: 'Link de resposta copiado. O colaborador precisa entrar na própria conta.',
          type: 'is-success',
        })
      } catch (e) {
        this.$buefy.dialog.alert({ title: 'Link de resposta', message: link })
      }
    },
    abrirCriacao() {
      this.novoCiclo = { nome: '', inicioEm: '', fimEm: '' }
      this.erroCriacao = ''
      this.modalCriacao = true
    },
    async criarCiclo() {
      this.erroCriacao = ''
      this.salvando = true
      try {
        if (!this.novoCiclo.inicioEm || !this.novoCiclo.fimEm) throw { data: { erro: 'Informe início e término.' } }
        await http.post('/api/rh/diagnostico/ciclos', {
          unidadeId: this.unidadeId,
          equipeId: this.equipeId,
          nome: this.novoCiclo.nome,
          inicioEm: new Date(this.novoCiclo.inicioEm).toISOString(),
          fimEm: new Date(this.novoCiclo.fimEm).toISOString(),
        })
        this.modalCriacao = false
        await this.carregarCiclos()
        this.$buefy.toast.open({ message: 'Ciclo aberto.', type: 'is-success' })
      } catch (e) {
        this.erroCriacao = e && e.data && e.data.erro ? e.data.erro : 'Não foi possível concluir a operação. Tente novamente.'
      } finally {
        this.salvando = false
      }
    },
    confirmarEncerramento(ciclo) {
      this.$buefy.dialog.confirm({
        title: 'Encerrar ciclo',
        message: `Encerrar ${ciclo.nome}? As respostas serão bloqueadas.`,
        confirmText: 'Encerrar',
        cancelText: 'Cancelar',
        type: 'is-warning',
        onConfirm: async () => {
          try {
            await http.post(`/api/rh/diagnostico/ciclos/${ciclo.id}/encerrar?unidadeId=${this.unidadeId}`)
            await this.carregarCiclos()
          } catch (e) {
            this.falha(e)
          }
        },
      })
    },
    async verResultado(ciclo) {
      try {
        this.resultado7D = null
        this.resultado = (
          await http.get(`/api/rh/diagnostico/ciclos/${ciclo.id}/resultado`, { params: { unidadeId: this.unidadeId } })
        ).data
        this.abaAtiva = 'resultados'
      } catch (e) {
        this.falha(e)
      }
    },
    async verResultado7D(ciclo) {
      try {
        this.resultado = null
        this.resultado7D = (
          await http.get(`/api/rh/diagnostico/ciclos/${ciclo.id}/resultado-7d`, { params: { unidadeId: this.unidadeId } })
        ).data
        this.abaAtiva = 'resultados'
      } catch (e) {
        this.falha(e)
      }
    },
    async comparar() {
      try {
        this.comparacao = (
          await http.get('/api/rh/diagnostico/comparar', {
            params: { unidadeId: this.unidadeId, antesId: this.antesId, depoisId: this.depoisId },
          })
        ).data
        this.abaComparacao = 'tabela'
      } catch (e) {
        this.falha(e)
      }
    },
    async compararEquipes() {
      try {
        this.comparacaoEquipes = (
          await http.get('/api/rh/diagnostico/comparar-equipes', {
            params: { unidadeId: this.unidadeId, cicloAId: this.cicloAId, cicloBId: this.cicloBId },
          })
        ).data
      } catch (e) {
        this.falha(e)
      }
    },
    async baixarRelatorio(formato) {
      try {
        const excel = formato === 'xlsx'
        const resposta = await http.get(`/api/rh/diagnostico/${excel ? 'relatorio-excel' : 'relatorio'}`, {
          params: { unidadeId: this.unidadeId, antesId: this.antesId, depoisId: this.depoisId },
          responseType: 'blob',
        })
        const url = URL.createObjectURL(resposta.data)
        const link = document.createElement('a')
        link.href = url
        link.download = `rh_evolucao_${this.antesId}_${this.depoisId}.${excel ? 'xlsx' : 'pdf'}`
        link.click()
        setTimeout(() => URL.revokeObjectURL(url), 1000)
      } catch (e) {
        this.falha(e)
      }
    },
    async baixarExcel(caminho, nome) {
      try {
        const resposta = await http.get(caminho, { params: { unidadeId: this.unidadeId }, responseType: 'blob' })
        const url = URL.createObjectURL(resposta.data)
        const link = document.createElement('a')
        link.href = url
        link.download = nome
        link.click()
        setTimeout(() => URL.revokeObjectURL(url), 1000)
      } catch (e) {
        this.falha(e)
      }
    },
    async abrirQuestionario(ciclo) {
      try {
        this.cicloRespostaId = ciclo.id
        this.tipoResposta = ciclo.tipo || '9d'
        this.questionario = (
          await http.get(`/api/rh/diagnostico/ciclos/${ciclo.id}/questionario`, {
            params: { unidadeId: this.unidadeId, tipo: this.tipoResposta },
          })
        ).data
        this.notas = {}
        this.consentimento = null
        this.$nextTick(() => document.getElementById('rh-questionario')?.scrollIntoView({ behavior: 'smooth' }))
      } catch (e) {
        this.falha(e)
      }
    },
    async enviarRespostas() {
      if (this.consentimento !== true || this.respostasPreenchidas !== this.totalQuestoes) return
      this.salvando = true
      try {
        await http.post(`/api/rh/diagnostico/ciclos/${this.cicloRespostaId}/respostas`, {
          unidadeId: this.unidadeId,
          tipo: this.tipoResposta,
          notas: this.notas,
        })
        this.questionario = null
        this.notas = {}
        await this.carregarMeusCiclos()
        this.$buefy.toast.open({ message: 'Respostas registradas. Obrigado!', type: 'is-success' })
      } catch (e) {
        this.falha(e)
      } finally {
        this.salvando = false
      }
    },
  },
}
</script>

<style scoped>
.rh-trend-row {
  display: grid;
  grid-template-columns: minmax(180px, 2fr) minmax(100px, 3fr) 52px 90px;
  align-items: center;
  gap: 12px;
  padding: 11px 0;
  border-bottom: 1px solid #edf0f4;
  font-size: 13px;
}
.rh-export-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 16px;
}
.rh-diagnostic {
  --navy: #0b1a3e;
  --blue: #007bff;
  --violet: #6610f2;
  --teal: #0e8fa8;
  --paper: #f4f3f0;
  background: var(--paper);
  min-height: 100vh;
  padding: 0 28px 48px;
  border-radius: 18px;
  color: #1c1d24;
  font-family: Montserrat, Arial, sans-serif;
}
.rh-kicker {
  letter-spacing: 0.15em;
  font-size: 11px;
  font-weight: 800;
  color: var(--teal);
  margin: 0;
}
.rh-card {
  background: #fff;
  border: 1px solid #e7e9ed;
  border-radius: 17px;
  padding: 24px;
  margin: 16px 0;
  box-shadow: 0 8px 20px rgba(11, 26, 62, 0.04);
}
.rh-card h2 {
  font-size: 22px;
  color: var(--navy);
  font-weight: 800;
  margin: 5px 0 0;
}
.rh-card-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 18px;
}
.rh-count {
  background: #eaf3ff;
  color: #2859a6;
  padding: 7px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
}
.rh-toolbar {
  display: flex;
  align-items: flex-end;
  gap: 16px;
}
.rh-toolbar .field {
  flex: 1;
  margin: 0;
}
.rh-empty {
  padding: 28px;
  text-align: center;
  color: #667085;
}
.rh-notice {
  padding: 17px 20px;
  border-radius: 12px;
  background: #eaf3ff;
  border: 1px solid #c8dffb;
  color: #224b80;
  margin: 14px 0;
}
.rh-result-top {
  display: flex;
  gap: 15px;
  margin-bottom: 22px;
}
.rh-result-top > div {
  min-width: 155px;
  background: #f7f9fc;
  padding: 20px;
  border-radius: 14px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.rh-result-top span,
.rh-result-top small {
  color: #667085;
  font-size: 12px;
}
.rh-result-top strong {
  font-size: 30px;
  color: var(--navy);
}
.rh-dimensions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 13px 24px;
}
.rh-dimension {
  display: grid;
  grid-template-columns: 140px 1fr 45px;
  align-items: center;
  gap: 10px;
  font-size: 13px;
}
.rh-track {
  background: #e7ebf1;
  border-radius: 999px;
  height: 9px;
  overflow: hidden;
}
.rh-track i {
  height: 100%;
  display: block;
  border-radius: 999px;
  background: linear-gradient(90deg, var(--teal), var(--blue));
}
.rh-compare-picker {
  display: flex;
  gap: 12px;
}
.rh-diagnostic-tabs ::v-deep > .tabs > ul > li {
  flex: none;
}
.rh-diagnostic-tabs ::v-deep > .tabs > ul > li > a {
  font-size: 14px;
  padding: 0.5em 0.9em;
}
.rh-compare-tabs {
  margin-top: 20px;
}
.rh-compare-bars {
  background: #171924;
  border-radius: 12px;
  color: #e5e5eb;
  padding: 12px;
}
.rh-bars-legend {
  color: #a9abb7;
  font-size: 12px;
  margin: 0 12px 4px;
}
.rh-bar-row {
  border-radius: 10px;
  padding: 12px;
}
.rh-bar-row:first-of-type {
  background: #232532;
}
.rh-bar-heading {
  align-items: baseline;
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}
.rh-bar-name {
  font-size: 16px;
  font-weight: 600;
}
.rh-bar-values {
  align-items: baseline;
  display: flex;
  flex-shrink: 0;
  gap: 16px;
  font-size: 14px;
}
.rh-bar-values .positive {
  color: #71caa8;
}
.rh-bar-values .negative {
  color: #ef8187;
}
.rh-bar-values strong {
  color: #e5e5eb;
  font-size: 20px;
  font-weight: 500;
}
.rh-bar-track {
  background: #424550;
  border-radius: 999px;
  height: 7px;
  position: relative;
}
.rh-bar-fill {
  border-radius: inherit;
  display: block;
  height: 100%;
}
.rh-bar-fill.critical {
  background: #e9787f;
}
.rh-bar-fill.attention {
  background: #e7be66;
}
.rh-bar-fill.healthy {
  background: #71caa8;
}
.rh-bar-previous {
  background: #e5e5eb;
  height: 15px;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 2px;
}
.rh-radar-panel {
  background: #171924;
  border-radius: 12px;
  padding: 12px 16px 22px;
}
.rh-radar-scroll {
  overflow-x: auto;
  text-align: center;
}
.rh-radar-chart {
  display: block;
  margin: auto;
  min-width: 700px;
  max-width: 900px;
  width: 100%;
}
.rh-radar-legend {
  color: #c7c8d2;
  display: flex;
  flex-wrap: wrap;
  font-size: 13px;
  gap: 14px 24px;
  justify-content: center;
  padding-top: 4px;
}
.rh-radar-legend > span {
  align-items: center;
  display: inline-flex;
  gap: 8px;
}
.rh-radar-key {
  border-top: 3px solid #a792ed;
  display: inline-block;
  width: 21px;
}
.rh-radar-key.previous {
  border-color: #a9aec1;
  border-top-style: dashed;
}
.rh-variation {
  font-size: 17px;
  margin: 22px 0;
}
.positive {
  color: #15805d;
}
.negative {
  color: #c34253;
}
.rh-muted {
  color: #667085;
  font-size: 13px;
}
.rh-question-group {
  border-top: 1px solid #e7e9ed;
  padding: 22px 0;
}
.rh-question-group h3 {
  font-size: 18px;
  color: var(--navy);
  font-weight: 800;
}
.rh-question {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 0;
  border-bottom: 1px solid #f0f2f4;
}
.rh-question p {
  margin: 0;
  max-width: 65%;
}
.rh-scale {
  display: flex;
  gap: 7px;
}
.rh-scale label {
  cursor: pointer;
}
.rh-scale input {
  position: absolute;
  opacity: 0;
}
.rh-scale span {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 1px solid #cfd8e3;
  border-radius: 9px;
  color: #4f5c6f;
  font-weight: 700;
}
.rh-scale input:checked + span {
  background: var(--blue);
  border-color: var(--blue);
  color: #fff;
}
.rh-scale input:focus-visible + span {
  outline: 2px solid var(--violet);
  outline-offset: 2px;
}
.rh-confirmacao {
  margin-top: 28px;
  padding: 20px 22px;
  border: 1px solid #bddcf9;
  border-left: 4px solid var(--blue);
  border-radius: 12px;
  background: #f0f7ff;
}
.rh-confirmacao-head {
  display: flex;
  align-items: center;
  gap: 11px;
  color: #1766ad;
}
.rh-confirmacao-kicker {
  margin: 0 0 3px;
  color: #1766ad;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.13em;
}
.rh-confirmacao h3 {
  margin: 0;
  color: var(--navy);
  font-size: 17px;
  font-weight: 800;
}
.rh-confirmacao-texto {
  margin: 12px 0 16px;
  color: #40536d;
  font-size: 13px;
  line-height: 1.5;
}
.rh-confirmacao-opcoes {
  display: flex;
  align-items: center;
  gap: 12px 24px;
  flex-wrap: wrap;
}
.rh-submit {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 24px;
}
.rh-submit span {
  font-size: 13px;
  color: #667085;
}
.rh-heat-scroll {
  overflow-x: auto;
}
.rh-heat {
  min-width: 850px;
}
.rh-heat th {
  font-size: 11px;
  color: #53627b;
  vertical-align: middle;
}
.rh-heat th:first-child {
  min-width: 130px;
}
.rh-heat th small {
  display: block;
  color: #8791a2;
  font-weight: 400;
  margin-top: 4px;
}
.rh-heat td {
  text-align: center;
  vertical-align: middle;
}
.rh-heat-cell {
  display: inline-block;
  min-width: 54px;
  padding: 8px 5px;
  border-radius: 8px;
  font-weight: 800;
}
.rh-heat-cell.critical {
  background: #fde3e5;
  color: #a3263d;
}
.rh-heat-cell.attention {
  background: #fff0cf;
  color: #945a00;
}
.rh-heat-cell.healthy {
  background: #dff5ea;
  color: #176843;
}
.rh-heat-empty {
  color: #a0a6b1;
}
@media (max-width: 800px) {
  .rh-dimensions {
    grid-template-columns: 1fr;
  }
  .rh-compare-picker {
    flex-direction: column;
  }
  .rh-question {
    align-items: flex-start;
    flex-direction: column;
  }
  .rh-question p {
    max-width: 100%;
  }
}
@media (max-width: 560px) {
  .rh-diagnostic {
    padding: 0 14px 30px;
  }
  .rh-card {
    padding: 18px;
  }
  .rh-toolbar {
    align-items: stretch;
    flex-direction: column;
  }
  .rh-result-top {
    flex-direction: column;
  }
  .rh-scale span {
    width: 33px;
    height: 33px;
  }
  .rh-confirmacao {
    padding: 18px;
  }
  .rh-confirmacao-opcoes {
    align-items: flex-start;
    flex-direction: column;
  }
  .rh-dimension {
    grid-template-columns: 100px 1fr 40px;
  }
  .rh-trend-row {
    grid-template-columns: 1fr 50px 80px;
  }
  .rh-trend-row .rh-track {
    grid-column: 1/-1;
    grid-row: 2;
  }
}
</style>
